import React, { useState, useEffect } from 'react';
import { Copy, Info, Search, Plus, Filter, Download, Edit, Trash2, Check, AlertCircle } from 'lucide-react';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';

export default function ComponentIdManagementDashboard() {
  const [components, setComponents] = useState([]);
  const [filteredComponents, setFilteredComponents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [screenFilter, setScreenFilter] = useState('all');
  const [copiedId, setCopiedId] = useState(null);
  const [showGenerator, setShowGenerator] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });

  // ID Generator Form State
  const [generatorForm, setGeneratorForm] = useState({
    componentType: '',
    screenContext: '',
    functionalPurpose: '',
    customSuffix: ''
  });

  // Component Types for categorization
  const componentTypes = [
    { value: 'page', label: 'Page', prefix: 'pg', color: 'blue' },
    { value: 'form', label: 'Form', prefix: 'frm', color: 'green' },
    { value: 'button', label: 'Button', prefix: 'btn', color: 'purple' },
    { value: 'modal', label: 'Modal', prefix: 'mdl', color: 'orange' },
    { value: 'table', label: 'Data Table', prefix: 'tbl', color: 'red' },
    { value: 'input', label: 'Input Field', prefix: 'inp', color: 'yellow' },
    { value: 'card', label: 'Card', prefix: 'crd', color: 'pink' },
    { value: 'nav', label: 'Navigation', prefix: 'nav', color: 'indigo' },
    { value: 'header', label: 'Header', prefix: 'hdr', color: 'cyan' },
    { value: 'footer', label: 'Footer', prefix: 'ftr', color: 'gray' }
  ];

  // Initialize with sample component registry
  useEffect(() => {
    const initialComponents = [
      {
        id: 'pg-dashboard-main',
        name: 'Dashboard Main Page',
        type: 'page',
        screen: 'Dashboard',
        description: 'Main dashboard overview page with KPI cards and charts',
        createdDate: '2026-01-01',
        usageCount: 1,
        implementationNotes: 'Primary landing page after login, displays real-time metrics'
      },
      {
        id: 'btn-login-submit',
        name: 'Login Submit Button',
        type: 'button',
        screen: 'Login',
        description: 'Primary action button for user authentication',
        createdDate: '2025-12-30',
        usageCount: 1,
        implementationNotes: 'Triggers authentication flow with email/password validation'
      },
      {
        id: 'tbl-accounts-main',
        name: 'Accounts Data Table',
        type: 'table',
        screen: 'Accounts',
        description: 'Main data grid displaying all account records',
        createdDate: '2025-12-31',
        usageCount: 1,
        implementationNotes: 'Sortable columns, pagination, and row actions for account management'
      },
      {
        id: 'mdl-invoice-create',
        name: 'Create Invoice Modal',
        type: 'modal',
        screen: 'Invoice Builder',
        description: 'Popup dialog for new invoice creation',
        createdDate: '2026-01-01',
        usageCount: 2,
        implementationNotes: 'Multi-step form with line item management and payment integration'
      },
      {
        id: 'frm-user-edit',
        name: 'User Edit Form',
        type: 'form',
        screen: 'User Management',
        description: 'Form for editing user profile and permissions',
        createdDate: '2025-12-31',
        usageCount: 1,
        implementationNotes: 'Includes role selection, profile fields, and activity log display'
      },
      {
        id: 'crd-kpi-revenue',
        name: 'Revenue KPI Card',
        type: 'card',
        screen: 'Dashboard',
        description: 'Dashboard card displaying total revenue metrics',
        createdDate: '2026-01-01',
        usageCount: 1,
        implementationNotes: 'Shows current month revenue with percentage change indicator'
      },
      {
        id: 'inp-search-contacts',
        name: 'Contact Search Input',
        type: 'input',
        screen: 'Contacts',
        description: 'Search field for filtering contact list',
        createdDate: '2025-12-30',
        usageCount: 1,
        implementationNotes: 'Real-time search with debounce, searches name, email, and phone'
      },
      {
        id: 'nav-sidebar-main',
        name: 'Main Sidebar Navigation',
        type: 'nav',
        screen: 'Global',
        description: 'Primary application navigation sidebar',
        createdDate: '2025-12-30',
        usageCount: 1,
        implementationNotes: 'Collapsible sidebar with role-based menu items and active state'
      }
    ];

    setComponents(initialComponents);
    setFilteredComponents(initialComponents);
  }, []);

  // Filter components based on search and filters
  useEffect(() => {
    let filtered = [...components];

    // Search filter
    if (searchQuery) {
      filtered = filtered?.filter(comp =>
        comp?.id?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        comp?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        comp?.screen?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered?.filter(comp => comp?.type === typeFilter);
    }

    // Screen filter
    if (screenFilter !== 'all') {
      filtered = filtered?.filter(comp => comp?.screen === screenFilter);
    }

    setFilteredComponents(filtered);
  }, [searchQuery, typeFilter, screenFilter, components]);

  // Get unique screens for filter dropdown
  const uniqueScreens = [...new Set(components?.map(c => c?.screen))];

  // Copy ID to clipboard
  const handleCopyId = async (id) => {
    try {
      await navigator?.clipboard?.writeText(id);
      setCopiedId(id);
      showAlert('Component ID copied to clipboard!', 'success');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      showAlert('Failed to copy ID', 'error');
    }
  };

  // Show component info modal
  const handleShowInfo = (component) => {
    setSelectedComponent(component);
    setShowInfoModal(true);
  };

  // Generate ID from form inputs
  const generateId = () => {
    const { componentType, screenContext, functionalPurpose, customSuffix } = generatorForm;
    
    if (!componentType || !functionalPurpose) {
      showAlert('Please select component type and enter functional purpose', 'error');
      return;
    }

    const typeObj = componentTypes?.find(t => t?.value === componentType);
    const prefix = typeObj?.prefix || 'cmp';
    const screen = screenContext ? `${screenContext?.toLowerCase()?.replace(/\s+/g, '-')}-` : '';
    const purpose = functionalPurpose?.toLowerCase()?.replace(/\s+/g, '-');
    const suffix = customSuffix ? `-${customSuffix?.toLowerCase()?.replace(/\s+/g, '-')}` : '';

    const generatedId = `${prefix}-${screen}${purpose}${suffix}`;
    
    // Check for duplicates
    const isDuplicate = components?.some(c => c?.id === generatedId);
    if (isDuplicate) {
      showAlert('This ID already exists! Please modify your inputs.', 'error');
      return;
    }

    // Add to components list
    const newComponent = {
      id: generatedId,
      name: `${functionalPurpose} ${typeObj?.label}`,
      type: componentType,
      screen: screenContext || 'Global',
      description: 'Auto-generated component ID',
      createdDate: new Date()?.toISOString()?.split('T')?.[0],
      usageCount: 0,
      implementationNotes: 'Newly generated component ID - implementation pending'
    };

    setComponents([newComponent, ...components]);
    showAlert(`Component ID "${generatedId}" generated successfully!`, 'success');
    
    // Reset form
    setGeneratorForm({
      componentType: '',
      screenContext: '',
      functionalPurpose: '',
      customSuffix: ''
    });
    setShowGenerator(false);
  };

  // Show alert notification
  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 3000);
  };

  // Export component registry as CSV
  const handleExport = () => {
    const csv = [
      ['Component ID', 'Component Name', 'Type', 'Screen', 'Created Date', 'Usage Count'],
      ...components?.map(c => [c?.id, c?.name, c?.type, c?.screen, c?.createdDate, c?.usageCount])
    ]?.map(row => row?.join(','))?.join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window?.URL?.createObjectURL(blob);
    const a = document?.createElement('a');
    a?.setAttribute('href', url);
    a?.setAttribute('download', 'component-ids-registry.csv');
    a?.click();

    showAlert('Component registry exported successfully!', 'success');
  };

  // Delete component from registry
  const handleDelete = (id) => {
    if (window?.confirm('Are you sure you want to delete this component ID?')) {
      setComponents(components?.filter(c => c?.id !== id));
      showAlert('Component ID deleted successfully', 'success');
    }
  };

  // Get color classes for component type
  const getTypeColor = (type) => {
    const typeObj = componentTypes?.find(t => t?.value === type);
    const color = typeObj?.color || 'gray';
    return {
      badge: `bg-${color}-100 text-${color}-800`,
      border: `border-${color}-200`
    };
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Component ID Management Dashboard</h1>
          <p className="text-gray-600">
            Central control center for tracking, managing, and organizing all component identifiers across LocalProCRM
          </p>
        </div>

        {/* Alert Notification */}
        {alert?.show && (
          <div className={`mb-6 p-4 rounded-lg border ${
            alert?.type === 'success' ?'bg-green-50 border-green-200 text-green-800' :'bg-red-50 border-red-200 text-red-800'
          } flex items-center justify-between`}>
            <div className="flex items-center gap-2">
              {alert?.type === 'success' ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
              <span>{alert?.message}</span>
            </div>
          </div>
        )}

        {/* Controls Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search by ID, name, or screen..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Filters and Actions */}
            <div className="flex flex-wrap items-center gap-3">
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e?.target?.value)}
                className="w-40"
              >
                <option value="all">All Types</option>
                {componentTypes?.map(type => (
                  <option key={type?.value} value={type?.value}>{type?.label}</option>
                ))}
              </Select>

              <Select
                value={screenFilter}
                onChange={(e) => setScreenFilter(e?.target?.value)}
                className="w-40"
              >
                <option value="all">All Screens</option>
                {uniqueScreens?.map(screen => (
                  <option key={screen} value={screen}>{screen}</option>
                ))}
              </Select>

              <Button 
                onClick={() => setShowGenerator(true)} 
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4" />
                ID Generator
              </Button>

              <Button 
                onClick={handleExport} 
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
            </div>
          </div>

          {/* Stats Row */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm text-blue-600 font-medium">Total Components</div>
              <div className="text-2xl font-bold text-blue-900">{components?.length}</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-sm text-green-600 font-medium">Pages</div>
              <div className="text-2xl font-bold text-green-900">
                {components?.filter(c => c?.type === 'page')?.length}
              </div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="text-sm text-purple-600 font-medium">Forms & Buttons</div>
              <div className="text-2xl font-bold text-purple-900">
                {components?.filter(c => c?.type === 'form' || c?.type === 'button')?.length}
              </div>
            </div>
            <div className="bg-orange-50 rounded-lg p-4">
              <div className="text-sm text-orange-600 font-medium">Tables & Modals</div>
              <div className="text-2xl font-bold text-orange-900">
                {components?.filter(c => c?.type === 'table' || c?.type === 'modal')?.length}
              </div>
            </div>
          </div>
        </div>

        {/* Components Registry Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Component ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Component Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Screen Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usage Count
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredComponents?.length > 0 ? (
                  filteredComponents?.map((component) => {
                    const colors = getTypeColor(component?.type);
                    return (
                      <tr key={component?.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <code className="text-sm font-mono text-gray-900 bg-gray-100 px-2 py-1 rounded">
                              {component?.id}
                            </code>
                            <button
                              onClick={() => handleCopyId(component?.id)}
                              className="text-blue-600 hover:text-blue-800 transition-colors"
                              title="Copy ID"
                            >
                              {copiedId === component?.id ? (
                                <Check className="w-4 h-4 text-green-600" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{component?.name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${colors?.badge}`}>
                            {componentTypes?.find(t => t?.value === component?.type)?.label || component?.type}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {component?.screen}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {component?.createdDate}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {component?.usageCount}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleShowInfo(component)}
                              className="text-gray-600 hover:text-gray-900"
                              title="View Details"
                            >
                              <Info className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(component?.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Component"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                      No components found matching your filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ID Generator Modal */}
        {showGenerator && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Component ID Generator</h2>
                <button 
                  onClick={() => setShowGenerator(false)} 
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Component Type *
                  </label>
                  <Select
                    value={generatorForm?.componentType}
                    onChange={(e) => setGeneratorForm({ ...generatorForm, componentType: e?.target?.value })}
                    className="w-full"
                  >
                    <option value="">Select component type</option>
                    {componentTypes?.map(type => (
                      <option key={type?.value} value={type?.value}>
                        {type?.label} ({type?.prefix}-)
                      </option>
                    ))}
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Screen Context (Optional)
                  </label>
                  <Input
                    type="text"
                    placeholder="e.g., Dashboard, Login, User Management"
                    value={generatorForm?.screenContext}
                    onChange={(e) => setGeneratorForm({ ...generatorForm, screenContext: e?.target?.value })}
                  />
                  <p className="text-xs text-gray-500 mt-1">The screen or page where this component will be used</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Functional Purpose *
                  </label>
                  <Input
                    type="text"
                    placeholder="e.g., Submit, Create, Edit, Display"
                    value={generatorForm?.functionalPurpose}
                    onChange={(e) => setGeneratorForm({ ...generatorForm, functionalPurpose: e?.target?.value })}
                  />
                  <p className="text-xs text-gray-500 mt-1">What this component does or represents</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom Suffix (Optional)
                  </label>
                  <Input
                    type="text"
                    placeholder="e.g., primary, secondary, alt"
                    value={generatorForm?.customSuffix}
                    onChange={(e) => setGeneratorForm({ ...generatorForm, customSuffix: e?.target?.value })}
                  />
                  <p className="text-xs text-gray-500 mt-1">Additional identifier if needed</p>
                </div>

                {/* Preview */}
                {generatorForm?.componentType && generatorForm?.functionalPurpose && (
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="text-sm font-medium text-blue-900 mb-2">Generated ID Preview:</div>
                    <code className="text-lg font-mono text-blue-700 bg-white px-3 py-2 rounded block">
                      {`${componentTypes?.find(t => t?.value === generatorForm?.componentType)?.prefix || 'cmp'}-${
                        generatorForm?.screenContext ? `${generatorForm?.screenContext?.toLowerCase()?.replace(/\s+/g, '-')}-` : ''
                      }${generatorForm?.functionalPurpose?.toLowerCase()?.replace(/\s+/g, '-')}${
                        generatorForm?.customSuffix ? `-${generatorForm?.customSuffix?.toLowerCase()?.replace(/\s+/g, '-')}` : ''
                      }`}
                    </code>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Button onClick={generateId} className="flex-1">
                    Generate ID
                  </Button>
                  <Button
                    onClick={() => setShowGenerator(false)}
                    className="flex-1 bg-gray-200 text-gray-800 hover:bg-gray-300"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Component Info Modal */}
        {showInfoModal && selectedComponent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Component Details</h2>
                <button 
                  onClick={() => setShowInfoModal(false)} 
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Component ID</label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-sm font-mono text-gray-900 bg-gray-100 px-3 py-2 rounded">
                      {selectedComponent?.id}
                    </code>
                    <button
                      onClick={() => handleCopyId(selectedComponent?.id)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Component Name</label>
                  <div className="text-gray-900 bg-gray-50 px-3 py-2 rounded">
                    {selectedComponent?.name}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full ${
                      getTypeColor(selectedComponent?.type)?.badge
                    }`}>
                      {componentTypes?.find(t => t?.value === selectedComponent?.type)?.label}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Screen Location</label>
                    <div className="text-gray-900">{selectedComponent?.screen}</div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <div className="text-gray-900 bg-gray-50 px-3 py-2 rounded">
                    {selectedComponent?.description}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Implementation Notes</label>
                  <div className="text-gray-900 bg-gray-50 px-3 py-2 rounded min-h-[80px]">
                    {selectedComponent?.implementationNotes}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Created Date</label>
                    <div className="text-gray-900">{selectedComponent?.createdDate}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Usage Count</label>
                    <div className="text-gray-900">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        {selectedComponent?.usageCount} instances
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={() => setShowInfoModal(false)}
                    className="flex-1"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}